import React, { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "../axios";
import { getDayType, DATE_BADGE } from "../utils/dayType";
import { useDayTypeVersion } from "../components/DayTypeProvider";
import ServiceCalendar from "../components/ServiceCalendar";
import MarqueeText from "../components/MarqueeText";
import DutyChain from "../components/DutyChain";
import VehicleChange from "../components/VehicleChange";
import { routeChipStyle, routeChips } from "../utils/routeColor";
import "../styles/analysis.css";

const DAY_TYPE_LABEL = { weekday: "平日", sat: "土曜", holiday: "日祝" };

// 所定運用の表。**成立していない種別も行を出す。**出さないと、
// 「所定が無い」のか「その種別を見ていない」のかが読めない
const DAY_TYPE_ROWS = [
  { key: "weekday", label: "平日" },
  { key: "sat", label: "土曜" },
  { key: "holiday", label: "日祝" },
];

/**
 * 運行日を N 日ずらす。
 *
 * **new Date("2026-08-27") を使わないこと。** ISO 形式の日付だけを渡すと
 * UTC として解釈され、日本時間では前日になる。年月日に分解して渡す。
 */
function shiftServiceDate(date, days) {
  const [y, m, d] = date.split("-").map(Number);
  const shifted = new Date(y, m - 1, d + days);
  const pad = (n) => String(n).padStart(2, "0");

  return `${shifted.getFullYear()}-${pad(shifted.getMonth() + 1)}-${pad(shifted.getDate())}`;
}

/**
 * 1 日ぶんの路線を、乗番をまたいで束ねる。
 *
 * 1 日に 2 つの乗番をこなしても、走った路線は同じことがある。
 * 2026-08-26 の 1911 は ア78 で千早線 6 便・ア17 で千早線 8 便なので、
 * 束ねて「千早(14便)」と出す。乗番ごとに分けると同じ名前が 2 つ並ぶ。
 *
 * 並びは時系列。先に走った乗番の、先に入った路線から。
 */
function mergeRoutes(duties) {
  const merged = new Map();

  duties.forEach((duty) => {
    (duty.routes ?? []).forEach((r) => {
      const found = merged.get(r.route_id);
      if (found) found.trip_count += r.trip_count;
      else merged.set(r.route_id, { ...r });
    });
  });

  return [...merged.values()];
}

/**
 * この車が日々どの乗番に入っていたかをカレンダーで見せる。
 *
 * 乗番ページの「この乗番に入った車両」と対になる形。あちらが
 * 「乗番から見た車」なら、こちらは「車から見た乗番」。
 *
 * 押すとその日の運行に切り替わる。乗番の詳細へは切り替えた先の
 * 見出しから辿れるので、**1セルに行き先を2つ置かない。**
 */
function DutyCalendar({ entries, onPick }) {
  const byDate = new Map(entries.map((e) => [e.service_date, e]));

  return (
    <ServiceCalendar
      weeks={5}
      renderCell={({ key }) => {
        const entry = byDate.get(key);
        if (!entry) return null;

        // 目撃はあるが乗番に結びつかなかった日。空白と混ぜると
        // 「走っていない」と読まれるので、印は出して灰色にする
        const duties = entry.duties ?? [];
        const unknown = duties.length === 0;
        const weak =
          entry.observed_trips > 0 && entry.matched_trips / entry.observed_trips < 0.3;

        // 乗番コードだけでは何線か思い浮かばないので路線を添える。
        // 最大2つ・3路線以上はまとめる規則は routeChips が持つ。
        // 全路線と便数は title に入れてある。
        // **乗番が分からない日も、走った路線までは出す。**
        // 「—」だけだと、丸一日 18 便走った日とまったく走っていない日が
        // 同じ顔になる。乗番が組めていない土曜がいまそれ。
        const seen = entry.seen_routes ?? [];
        // 改正で消えた乗番（retired）は路線を持たないので、目撃から数えた路線で代える
        const merged = mergeRoutes(duties);
        const routes = unknown || merged.length === 0 ? seen : merged;
        const chips = routeChips(routes);

        return (
          <button
            className={`anl-cal__duty${unknown ? " anl-cal__duty--unknown" : weak ? " anl-cal__duty--weak" : ""}`}
            onClick={() => onPick(entry)}
            title={`${key}（${DAY_TYPE_LABEL[entry.day_type] ?? entry.day_type}） · ${
              unknown
                ? entry.observed_trips > 0
                  ? `乗番は特定できていません（走ったことは分かっています）`
                  : "目撃がありません"
                : duties
                    .map((d) => `${d.duty_code}${d.retired ? "［改正前のダイヤ］" : ""}(${d.matched_trips}便)`)
                    .join(" → ")
            }${routes.length ? ` · ${routes.map((r) => `${r.route}(${r.trip_count}便)`).join(" ")}` : ""}${
              unknown ? ` · 目撃${entry.observed_trips}便` : ` · 目撃${entry.observed_trips}便中${entry.matched_trips}便が一致`
            }`}
          >
            {/* **1 日に複数の乗番をこなした日は縦に積む。** 横に「ア78→ア17」と
                並べるとスマホの狭いセルに収まらない。矢印は三角形（高さ4px）で、
                行間をほとんど使わずに順序だけを示す。 */}
            <span className="anl-cal__code">
              {unknown
                ? /* 乗番は出せないが、何便走ったかは言える。まったく走って
                     いない日（観測ゼロ）とは分けたい */
                  entry.observed_trips > 0
                  ? <span className="anl-cal__code-line">{entry.observed_trips}便</span>
                  : "—"
                : duties.map((d, i) => (
                    <span key={d.duty_id ?? `retired-${i}`}>
                      {i > 0 && <span className="anl-cal__code-arrow" aria-hidden="true" />}
                      {/* 改正で消えた乗番は、いまの同じ記号と別物。印を付けて見分ける */}
                      <span className={`anl-cal__code-line${d.retired ? " anl-cal__code-line--retired" : ""}`}>
                        {d.duty_code}
                        {d.retired && <span className="anl-cal__retired">旧</span>}
                      </span>
                    </span>
                  ))}
            </span>
            {chips.map((c) => (
              <span
                key={c.key}
                className={`anl-cal__route${c.many ? " anl-cal__route--many" : ""}`}
                style={c.style}
              >
                {c.label}
              </span>
            ))}
          </button>
        );
      }}
    />
  );
}

const CONFIDENCE_CLASS = {
  高: "anl-badge--high",
  中: "anl-badge--mid",
  低: "anl-badge--low",
  不明: "anl-badge--unknown",
};

// この日数以上あいたら「久しぶり」「長期未目撃」として知らせる
const LONG_ABSENCE_DAYS = 30;


// 便の状態。凡例と本体で同じものを使う。
// 文字（●○）で説明すると画面の印と食い違うので、凡例も同じ印を描く。
const TRIP_STATES = [
  { key: "observed", label: "目撃あり", note: "当該車両の運行を確認できた便" },
  { key: "other", label: "別の車が担当", note: "同じ乗番だが、別の車両が運行した便" },
  { key: "forecast", label: "これから", note: "未発車の便（予測）" },
  { key: "missing", label: "目撃なし", note: "目撃情報が無い便。運行していないとは限りません" },
];

function tripState(trip, isForecast) {
  if (trip.observed) return "observed";
  if (trip.other_bus) return "other";
  return isForecast ? "forecast" : "missing";
}

/** 実際に出ている状態だけを、本体と同じ印で並べる。 */
function TimelineLegend({ states }) {
  return (
    <ul className="anl-legend">
      {TRIP_STATES.filter((s) => states.has(s.key)).map((s) => (
        <li key={s.key} className={`anl-legend__item anl-trip--${s.key}`}>
          <span className="anl-trip__mark" aria-hidden="true" />
          <span className="anl-legend__label">{s.label}</span>
          <span className="anl-legend__note">{s.note}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * 乗番 1 本ぶんのカード。要約・タイムライン・車両交代・続きをまとめる。
 *
 * **1 日に 2 つ以上の乗番をこなす車がある**ので、乗番の数だけ積む。
 * 主に走った乗番だけ出すと、応援に入った先が見えなくなる。
 */
function DutyCard({ block, busDepot, serviceDate, upcomingIds, navigate }) {
  const { duty, timeline } = block;
  const states = new Set(timeline.map((t) => tripState(t, upcomingIds.has(t.trip_id))));

  return (
    <div className={`anl-duty${block.is_current ? " anl-duty--current" : ""}`}>
      <div className="anl-card">
        <div className="anl-card__header">
          <span style={{ display: "inline-flex", alignItems: "center", gap: ".375rem", flexWrap: "wrap" }}>
            <button
              className="anl-link"
              style={{ fontWeight: 700, fontSize: ".9375rem" }}
              onClick={() => navigate(`/analysis/duties/${duty.id}`)}
            >
              乗番{duty.code}
            </button>
            {block.is_current && <span className="anl-badge anl-badge--now">現在</span>}
            {!block.is_primary && !block.is_current && (
              <span className="anl-badge anl-badge--foreign">応援</span>
            )}
          </span>
          <span className={`anl-badge ${CONFIDENCE_CLASS[block.confidence_label] ?? ""}`}>
            確度{block.confidence_label}
          </span>
        </div>

        <div className="anl-card__body">
          <div className="anl-stats">
            <div>
              <div className="anl-stat__label">一致</div>
              <div className="anl-stat__value">{block.matched_trips} / {duty.trip_count}便</div>
              <div className="anl-stat__note">乗番の便のうち目撃できた数</div>
            </div>
            <div>
              <div className="anl-stat__label">一致率</div>
              <div className="anl-stat__value">{Math.round(block.confidence * 100)}%</div>
              <div className="anl-stat__note">目撃のうち当該乗番の便が占める割合</div>
            </div>
            <div>
              <div className="anl-stat__label">稼働時間</div>
              <div className="anl-stat__value">{duty.first_departure}〜{duty.last_departure}</div>
              {/* 終発「時刻」であって運行が終わる時刻ではない。
                  最終便が走り終わる時刻はデータに無い */}
              <div className="anl-stat__note">※始発地発車時刻</div>
            </div>
          </div>
        </div>

        <div className="anl-card__body" style={{ borderTop: "1px solid var(--anl-line)" }}>
          <ul className="anl-timeline">
            {timeline.map((trip) => (
              <TripRow
                key={trip.trip_id}
                trip={trip}
                isForecast={upcomingIds.has(trip.trip_id)}
                onPickBus={(n) =>
                  navigate(`/analysis/buses/${encodeURIComponent(n)}?date=${serviceDate}&depot=${encodeURIComponent(busDepot)}`)
                }
              />
            ))}
          </ul>
          <TimelineLegend states={states} />
        </div>
      </div>

      <VehicleChange
        change={block.vehicle_change}
        dutyCode={duty.code}
        dutyId={duty.id}
        serviceDate={serviceDate}
        depot={busDepot}
        navigate={navigate}
      />

      {block.next_duties?.length > 0 && (
        <div className="anl-card">
          <div className="anl-card__header">
            乗番{duty.code}（{duty.last_departure}）のあと
            {block.next_duties.length > 1 && (
              <span className="anl-badge anl-badge--mid">続きは{block.next_duties.length}通り</span>
            )}
          </div>
          <div className="anl-card__body">
            <DutyChain
              self={duty}
              selfLabel="いまの乗番"
              next={block.next_duties}
              onOpen={(id) => navigate(`/analysis/duties/${id}`)}
            />
            <p className="anl-note">
              {block.next_duties.length > 1
                ? "続きが複数あります。日によって移る乗番が異なるため、いずれになるかは判別できません。"
                : "当該乗番の後、同じ車両が続けて担当した乗番です。"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function TripRow({ trip, isForecast, onPickBus }) {
  // 「目撃なし」と「別の車が走った」はまったく違う。混ぜると観測の穴に見える
  const state = tripState(trip, isForecast);
  const weakEdge = trip.edge_share !== null && trip.edge_share < 0.8;

  return (
    <li className={`anl-trip anl-trip--${state}`}>
      <span className="anl-trip__mark" aria-hidden="true" />
      <span className="anl-trip__time">{trip.departure_time}</span>
      <span className="anl-trip__where">
        <span className="anl-trip__route" style={routeChipStyle(trip.route_color)}>
          {trip.route}
        </span>
        <span className="anl-trip__start">{trip.start}発</span>
        {weakEdge && (
          <span className="anl-trip__tag anl-trip__tag--warn">
            接続率が低い（{Math.round(trip.edge_share * 100)}%）
          </span>
        )}
      </span>
      {/* 行先番号。**余った幅だけを使う。** 他の列を押しのけないよう
          この列だけが伸び縮みし、入りきらないときは中の文字を流す。 */}
      <span className="anl-trip__no">
        {trip.route_no && (
          <span className="anl-no" title={trip.route_no}>
            <MarqueeText text={trip.route_no} />
          </span>
        )}
      </span>
      <span className="anl-trip__right">
        {trip.observed ? (
          "目撃あり"
        ) : trip.other_bus ? (
          <button className="anl-link" onClick={() => onPickBus(trip.other_bus)}>
            {trip.other_bus}が担当
          </button>
        ) : isForecast ? (
          "これから"
        ) : (
          "目撃なし"
        )}
      </span>
    </li>
  );
}

export default function AnalysisBusPage() {
  useDayTypeVersion();

  const { busNumber } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const date = searchParams.get("date") || "";
  const depot = searchParams.get("depot") || "";

  const [duty, setDuty] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [candidates, setCandidates] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setCandidates(null);

    const query = new URLSearchParams();
    if (date) query.set("date", date);
    if (depot) query.set("depot", depot);
    const suffix = query.toString() ? `?${query}` : "";

    try {
      const [dutyRes, statsRes] = await Promise.all([
        axios.get(`/analysis/buses/${encodeURIComponent(busNumber)}/duty${suffix}`),
        axios.get(`/analysis/buses/${encodeURIComponent(busNumber)}/stats${depot ? `?depot=${encodeURIComponent(depot)}` : ""}`),
      ]);
      setDuty(dutyRes.data);
      setStats(statsRes.data);
    } catch (e) {
      const data = e.response?.data;
      if (data?.candidates) {
        setCandidates(data.candidates);
        setError(data.message);
      } else {
        setError(data?.message ?? "取得に失敗しました");
      }
      setDuty(null);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [busNumber, date, depot]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="anl-page"><p className="anl-empty">読み込み中…</p></div>;

  if (error) {
    return (
      <div className="anl-page">
        <button className="anl-back" onClick={() => navigate("/analysis")}>運用解析</button>
        <div className="anl-warning">{error}</div>
        {candidates && (
          <div className="anl-depot-grid">
            {candidates.map((c) => (
              <button
                key={c.depot}
                className="anl-depot-card"
                onClick={() => setSearchParams({ ...(date ? { date } : {}), depot: c.depot })}
              >
                <div className="anl-depot-card__name">{c.bus_number}</div>
                <div className="anl-depot-card__meta">{c.depot}営業所</div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  const serviceDate = duty.service_date;
  const badge = DATE_BADGE[getDayType(serviceDate)];
  const identified = duty.duty !== null;
  // 営業所の指定は日付を変えても引き継ぐ。落とすと車号が
  // 複数営業所にある場合に候補選択へ戻ってしまう
  const goToDate = (d) => setSearchParams({ date: d, ...(depot ? { depot } : {}) });
  const upcoming = duty.upcoming ?? [];
  const upcomingIds = new Set(upcoming.map((t) => t.trip_id));

  return (
    <div className="anl-page">
      <button className="anl-back" onClick={() => navigate("/analysis")}>運用解析</button>

      <div className="anl-header">
        <h2 className="anl-title">
          {duty.bus.bus_number}
          <span className="anl-badge anl-badge--depot">{duty.bus.depot}</span>
          {identified && <span className="anl-badge anl-badge--code">{duty.duty.code}</span>}
        </h2>
        <p className="anl-subtitle">
          <span
            style={{
              background: badge.bg, color: badge.color, border: `1px solid ${badge.border}`,
              padding: "0 .375rem", borderRadius: ".25rem", marginRight: ".375rem",
            }}
          >
            {serviceDate}（{DAY_TYPE_LABEL[duty.day_type] ?? duty.day_type}）
          </span>
          {duty.is_today ? "本日の運行日" : "過去の運行日"}
        </p>
      </div>

      {/* 日付の切り替え */}
      <div className="anl-search">
        <button
          className="anl-daynav anl-daynav--prev"
          onClick={() => goToDate(shiftServiceDate(serviceDate, -1))}
          title="前日"
          aria-label="前日"
        >
        </button>
        <input
          type="date"
          className="anl-input"
          value={serviceDate}
          onChange={(e) => goToDate(e.target.value)}
        />
        <button
          className="anl-daynav anl-daynav--next"
          onClick={() => goToDate(shiftServiceDate(serviceDate, 1))}
          /* 先の運行日は目撃が存在しないので進ませない */
          disabled={duty.is_today}
          title={duty.is_today ? "本日より先は表示できません" : "翌日"}
          aria-label="翌日"
        >
        </button>
        {!duty.is_today && (
          <button className="anl-btn" onClick={() => setSearchParams(depot ? { depot } : {})}>
            本日に戻る
          </button>
        )}
      </div>

      {/* A・B: 初入線 / 久しぶり。運用を追う人がいちばん面白がるところ */}
      {(duty.route_history ?? [])
        .filter((h) => h.is_first_time || (h.days_since_previous ?? 0) >= LONG_ABSENCE_DAYS)
        .map((h) => (
          <div
            key={h.route_id}
            className="anl-notice anl-notice--find"
            onClick={() => navigate(`/analysis/routes/${h.route_id}`)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && navigate(`/analysis/routes/${h.route_id}`)}
          >
            {h.is_first_time ? (
              <>
                <strong>初入線</strong> — {duty.bus.bus_number}が{h.route}に入るのは、
                記録上初めてです。
              </>
            ) : (
              <>
                <strong>{h.days_since_previous}日ぶり</strong> — {duty.bus.bus_number}が{h.route}に
                入るのは{h.days_since_previous}日ぶりです（前回{h.previous_seen_on}）。
              </>
            )}
          </div>
        ))}

      {/* C: 長期未目撃 */}
      {stats && stats.days_since_last_seen !== null && stats.days_since_last_seen >= LONG_ABSENCE_DAYS && (
        <div className="anl-notice anl-notice--quiet">
          <strong>{stats.days_since_last_seen}日間目撃なし</strong> — 入場・転属・休車などの可能性があります。
          最終目撃は{stats.last_seen_at}です。
        </div>
      )}

      {duty.before_revision ? (
        /* **改正前の日を、いまの乗番に当てはめない。**いまの乗番は改正後の
           ダイヤで組んであるので、当てはめると別のダイヤの乗番が付く。
           その日に割り当てた記号だけを出す */
        <div className="anl-notice anl-notice--quiet">
          <strong>{duty.before_revision}のダイヤ改正より前の運行日です。</strong>
          {(duty.recorded_duties ?? []).length > 0
            ? `この日は改正前のダイヤの乗番 ${duty.recorded_duties.join(" → ")} に入っていました。`
            : "この日の乗番は記録されていません。"}
          いまの乗番は改正後のダイヤで組んでいるため、便の並びはお見せできません。
        </div>
      ) : !identified ? (
        <div className="anl-warning">
          当日の運用は特定できていません。
          {duty.observed_trips === 0
            ? "当該車両の目撃情報がありません。"
            : `目撃は${duty.observed_trips}件ありますが、乗番と結びつきませんでした。`}
          {duty.unknown_trips > 0 && ` うち${duty.unknown_trips}件はダイヤに存在しない時刻です。`}
        </div>
      ) : (
        <>
          {/* 次はここ */}
          {duty.is_today && upcoming.length > 0 && (
            <div className="anl-next">
              <div className="anl-next__label">次の便（予測）</div>
              <div className="anl-next__value">
                {upcoming[0].departure_time} {upcoming[0].start}発 · {upcoming[0].route}
              </div>
              <p className="anl-note">
                残り{upcoming.length}便。乗番からの推定のため、運用変更や運休が生じた場合は実際と異なります。
              </p>
            </div>
          )}

          {/* 乗番ごとにカードを積む。1日に2つ以上こなす車があるため、
              主に走った乗番だけ出すと片方が見えなくなる */}
          {(duty.duties ?? []).map((block) => (
            <DutyCard
              key={block.duty.id}
              block={block}
              busDepot={duty.bus.depot}
              serviceDate={serviceDate}
              upcomingIds={upcomingIds}
              navigate={navigate}
            />
          ))}
        </>
      )}

      {/* 統計 */}
      {stats && (
        <>
          <div className="anl-card">
            <div className="anl-card__header">車両の記録</div>
            <div className="anl-card__body">
              <div className="anl-stats">
                <div>
                  <div className="anl-stat__label">目撃総数</div>
                  <div className="anl-stat__value">{stats.total_sightings.toLocaleString()}</div>
                </div>
                <div>
                  <div className="anl-stat__label">稼働日数</div>
                  <div className="anl-stat__value">
                    {stats.active_days_30.active} / {stats.active_days_30.window}日
                  </div>
                  <div className="anl-stat__note">直近30日で目撃された日</div>
                </div>
                <div>
                  <div className="anl-stat__label">最終目撃</div>
                  <div className="anl-stat__value">
                    {stats.days_since_last_seen === null
                      ? "—"
                      : stats.days_since_last_seen === 0
                        ? "本日"
                        : `${stats.days_since_last_seen}日前`}
                  </div>
                  <div className="anl-stat__note">{stats.last_seen_at ?? ""}</div>
                </div>
                <div>
                  <div className="anl-stat__label">初目撃</div>
                  <div className="anl-stat__value" style={{ fontSize: ".9375rem" }}>
                    {stats.first_seen_at?.slice(0, 10) ?? "—"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 所定運用。**ダイヤ種別ごとに別の乗番**なので、3 つ並べて出す。
              成立していない種別は「—」で、それが「所定が無い」のか
              「まだ決められない」のかは注記で断る。 */}
          <div className="anl-card">
            <div className="anl-card__header">所定運用</div>
            <div className="anl-table-wrap">
              <table className="anl-table">
                <thead>
                  <tr>
                    <th>ダイヤ</th>
                    <th>乗番</th>
                    {/* 中身は営業所。応援で他所の乗番の所定になることがある */}
                    <th>営業所</th>
                    <th className="anl-table__num">便数</th>
                    <th>始発</th>
                    <th>終発</th>
                    <th className="anl-table__num">実績</th>
                  </tr>
                </thead>
                <tbody>
                  {DAY_TYPE_ROWS.map(({ key, label }) => {
                    const rows = (stats.regular_duties ?? []).filter((d) => d.day_type === key);

                    if (rows.length === 0) {
                      return (
                        <tr key={key}>
                          <td>{label}</td>
                          <td colSpan={6} style={{ color: "#9ca3af" }}>—</td>
                        </tr>
                      );
                    }

                    return rows.map((d, i) => (
                      <tr key={`${key}-${d.duty_id}`}>
                        {/* 同じダイヤ種別で 2 本になることがある（乗番が短い日）。
                            ラベルは先頭行だけに出す */}
                        <td>{i === 0 ? label : ""}</td>
                        <td>
                          <button
                            className="anl-link"
                            onClick={() => navigate(`/analysis/duties/${d.duty_id}`)}
                          >
                            {d.code}
                          </button>
                        </td>
                        <td>{d.depot}</td>
                        <td className="anl-table__num">{d.trip_count}</td>
                        <td className="anl-table__num">{d.first_departure}</td>
                        <td className="anl-table__num">{d.last_departure}</td>
                        <td className="anl-table__num" title={`${d.window_to}までの直近${d.window_days}営業日`}>
                          {d.days}/{d.window_days}
                        </td>
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            </div>
            <div className="anl-card__body" style={{ borderTop: "1px solid var(--anl-line)" }}>
              <p className="anl-note">
                直近10営業日のうち6日以上この車両が入っていた乗番です。
                <strong>「—」は所定が無いという意味ではありません。</strong>
                日替わりで運用されている場合も、観測が10営業日分たまっていない場合も「—」になります。
              </p>
            </div>
          </div>

          {stats.top_routes.length > 0 && (
            <div className="anl-card">
              <div className="anl-card__header">運用の多い路線<span style={{ fontWeight: 400, color: "#6b7280" }}>直近90日</span></div>
              <div className="anl-table-wrap">
                <table className="anl-table">
                  <thead>
                    <tr><th>路線</th><th>路線名</th><th className="anl-table__num">日数</th><th></th></tr>
                  </thead>
                  <tbody>
                    {stats.top_routes.map((r) => (
                      <tr key={r.route_id}>
                        <td>
                          <button className="anl-link" onClick={() => navigate(`/analysis/routes/${r.route_id}`)}>
                            {r.route}
                          </button>
                        </td>
                        <td style={{ whiteSpace: "normal" }}>{r.route_full_name}</td>
                        <td className="anl-table__num">{r.count}</td>
                        <td>
                          {r.is_foreign && (
                            <span className="anl-badge anl-badge--foreign">{r.route_depot}の路線</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {stats.recent_duties.length > 0 && (
            <div className="anl-card">
              <div className="anl-card__header">乗番の履歴<span style={{ fontWeight: 400, color: "#6b7280" }}>直近5週</span></div>
              <div className="anl-card__body">
                <DutyCalendar
                  entries={stats.recent_duties}
                  onPick={(d) => setSearchParams({ date: d.service_date, ...(depot ? { depot } : {}) })}
                />
                <p className="anl-note">
                  各セルは当日この車両が入っていた乗番です。押すとその日の運行に切り替わります。
                  <strong>1日に複数の乗番をこなした日</strong>は、走った順に上から縦に並びます。
                  路線と便数は<strong>この車両が受け持った区間</strong>のもので、乗番全体ではありません
                  （途中から入った日は、それより前を別の車両が走っています）。
                  淡い緑は目撃できた便が少ない日を示します。
                  <strong>灰色で「N便」</strong>と出る日は、乗番までは特定できていないものの、
                  その路線をN便走ったことは分かっている日です（土曜など、乗番がまだ組み立てられて
                  いないダイヤ種別で出ます）。「—」は目撃そのものが無い日です。
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
